import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConsultationForm = ({ service }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    newsletter: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const projectTypes = [
    { value: 'new-implementation', label: 'New Implementation' },
    { value: 'system-upgrade', label: 'System Upgrade' },
    { value: 'consultation', label: 'Strategic Consultation' },
    { value: 'audit', label: 'Technical Audit' },
    { value: 'training', label: 'Team Training' }
  ];

  const budgetRanges = [
    { value: '10k-25k', label: '$10K - $25K' },
    { value: '25k-50k', label: '$25K - $50K' },
    { value: '50k-100k', label: '$50K - $100K' },
    { value: '100k-250k', label: '$100K - $250K' },
    { value: '250k+', label: '$250K+' }
  ];

  const timelineOptions = [
    { value: '1-3-months', label: '1-3 months' },
    { value: '3-6-months', label: '3-6 months' },
    { value: '6-12-months', label: '6-12 months' },
    { value: '12+ months', label: '12+ months' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-xl shadow-soft border border-border p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Consultation Request Submitted!
        </h3>
        <p className="text-muted-foreground mb-6">
          Thank you for your interest in our {service.title} services. Our team will review your requirements and contact you within 24 hours.
        </p>
        <Button 
          variant="outline" 
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              name: '',
              email: '',
              company: '',
              phone: '',
              projectType: '',
              budget: '',
              timeline: '',
              description: '',
              newsletter: false
            });
          }}
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft border border-border p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
          <Icon name="MessageSquare" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Request Consultation
          </h3>
          <p className="text-sm text-muted-foreground">
            Get expert guidance for your {service.title} needs
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="John Doe"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />
          
          <Input
            label="Email Address"
            type="email"
            placeholder="john@company.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            type="text"
            placeholder="Your Company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            required
          />
          
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />
        </div>

        <Select
          label="Project Type"
          placeholder="Select project type"
          options={projectTypes}
          value={formData.projectType}
          onChange={(value) => handleInputChange('projectType', value)}
          required
        />

        <div className="grid md:grid-cols-2 gap-4">
          <Select
            label="Budget Range"
            placeholder="Select budget range"
            options={budgetRanges}
            value={formData.budget}
            onChange={(value) => handleInputChange('budget', value)}
            required
          />
          
          <Select
            label="Timeline"
            placeholder="Select timeline"
            options={timelineOptions}
            value={formData.timeline}
            onChange={(value) => handleInputChange('timeline', value)}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Project Description
          </label>
          <textarea
            rows={4}
            placeholder="Tell us about your project requirements, goals, and any specific challenges you're facing..."
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            required
          />
        </div>

        <Checkbox
          label="Subscribe to our newsletter for AI insights and updates"
          checked={formData.newsletter}
          onChange={(e) => handleInputChange('newsletter', e.target.checked)}
        />

        <Button 
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="left"
        >
          {isSubmitting ? 'Submitting Request...' : 'Request Consultation'}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          By submitting this form, you agree to our privacy policy and terms of service.
        </p>
      </form>
    </div>
  );
};

export default ConsultationForm;